import React, { useState } from "react";
import styled from "styled-components";
import concedidoPqn from "../../assets/concedido-pequeno.png";
import { cpf as validateCpf } from "../../utils/validation";
import { usePopup } from "../common/Popup";
import {
  getDiasSemanaSolicitacao,
  getPeriodoInicio,
  getPeriodoTermino,
} from "./VagaResumoCard";

const DIAS_SEMANA = [
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
  "Domingo",
];

const parseHorario = (horario = "") => {
  const match = String(horario).match(/(\d{1,2}:\d{2})\s*[-–]\s*(\d{1,2}:\d{2})/);
  if (!match) return { horarioInicio: "", horarioFim: "" };
  return { horarioInicio: match[1], horarioFim: match[2] };
};

const buildFormState = (solicitacao, initialData = null) => {
  const diasSemanaDefault = getDiasSemanaSolicitacao(solicitacao?.diasSemana);

  if (!initialData) {
    const horarioInicio = solicitacao?.horaInicio || "";
    const horarioFim = solicitacao?.horaFim || "";
    return {
      nome: "",
      cpf: "",
      dataNascimento: "",
      numeroMatricula: "",
      horarioInicio,
      horarioFim,
      horarioRealizacao:
        horarioInicio && horarioFim ? `${horarioInicio} - ${horarioFim}` : "",
      diasSemana: diasSemanaDefault,
    };
  }

  const parsedHorario = parseHorario(initialData.horarioRealizacao);
  return {
    nome: initialData.nome || "",
    cpf: initialData.cpf || "",
    dataNascimento: initialData.dataNascimento || "",
    numeroMatricula: initialData.numeroMatricula || "",
    diasSemana: initialData.diasSemana || diasSemanaDefault,
    horarioRealizacao: initialData.horarioRealizacao || "",
    ...parsedHorario,
  };
};

const StatusBar = styled.div`
  font-family: "Public Sans", sans-serif;
  border-top: 1px solid #ccc;
  background-color: #f2f0f0;
  display: flex;
  align-items: center;
  padding: 12px 40px;
  gap: 20px;
`;

const SpecialtyName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #6ab8a8;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const StatusBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  color: #555555;
  font-weight: 500;

  img {
    width: 28px;
    height: 28px;
  }

  em {
    font-weight: 700;
    color: #2e7d32;
    font-style: italic;
  }
`;

const FormWrapper = styled.div`
  font-family: "Public Sans", sans-serif;
  padding: 24px 30px 40px 40px;
  max-width: 860px;
`;

const FormHeader = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #ff9f1f;
  margin-bottom: 18px;
`;

const FieldGroup = styled.div`
  margin-bottom: 14px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 15px;
  font-weight: 700;
  color: #333333;
  margin-bottom: 4px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #b9b9b9;
  border-radius: 6px;
  font-size: 15px;
  color: #555555;
  background: #ffffff;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #6ab8a8;
  }
`;

const ReadOnlyInput = styled(FormInput)`
  background: #f5f5f5;
  cursor: not-allowed;
`;

const TwoColumnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 14px;
`;

const HorarioRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  ${FormInput} {
    flex: 1;
  }
`;

const HorarioSeparator = styled.span`
  color: #555555;
  font-weight: 600;
`;

const FieldHint = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #777777;
`;

const DaysWrapper = styled.div`
  margin-bottom: 18px;
`;

const DaysList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 6px;
`;

const DayLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  color: #555555;
  user-select: none;
  cursor: pointer;

  input[type="checkbox"] {
    appearance: none;
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    border: 1px solid #4a4a4a;
    border-radius: 2px;
    background: #ffffff;
    position: relative;
    cursor: pointer;
    margin: 0;
  }

  input[type="checkbox"]:checked {
    background: #6ab8a8;
    border-color: #4a4a4a;
  }

  input[type="checkbox"]:checked::after {
    content: "";
    position: absolute;
    left: 4px;
    top: 1px;
    width: 4px;
    height: 8px;
    border: solid #000000;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const FormFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
`;

const VoltarButton = styled.button`
  background-color: #999;
  color: #ffffff;
  border: none;
  padding: 10px 26px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 8px;
  box-shadow: 0px 4px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;

  &:hover {
    background-color: #888;
  }
`;

const ProximoButton = styled.button`
  background: #6ab8a8;
  color: #ffffff;
  border: none;
  padding: 10px 26px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 8px;
  box-shadow: 0px 4px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;

  &:hover {
    background: #5da89a;
  }
`;

const CadastrarAlunoForm = ({ solicitacao, initialData, onProximo, onVoltar }) => {
  const popup = usePopup();
  const [form, setForm] = useState(() => buildFormState(solicitacao, initialData));

  const especialidade =
    solicitacao?.vaga?.unidadeAssistencial?.especialidade?.nome || "Vaga";
  const localRealizacao = solicitacao?.vaga?.estabelecimentoSaude?.nome || "";
  const inicioEstagio = getPeriodoInicio(solicitacao?.periodos);
  const terminoEstagio = getPeriodoTermino(solicitacao?.periodos);
  const cargaHorariaTotal = solicitacao?.horasTotaisPorAluno ?? "";

  const handleChange = (field) => (e) => {
    let value = e.target.value;
    if (field === "cpf") {
      value = value.replace(/\D/g, "").slice(0, 11);
    }
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleDia = (dia) => {
    setForm((prev) => {
      const selected = prev.diasSemana.includes(dia);
      return {
        ...prev,
        diasSemana: selected
          ? prev.diasSemana.filter((d) => d !== dia)
          : [...prev.diasSemana, dia],
      };
    });
  };

  const handleSubmit = async () => {
    const horarioRealizacao =
      form.horarioInicio && form.horarioFim
        ? `${form.horarioInicio} - ${form.horarioFim}`
        : "";

    const payload = {
      ...form,
      horarioRealizacao,
    };

    const required = ["nome", "cpf", "dataNascimento", "numeroMatricula", "horarioRealizacao"];
    const missing = required.filter(
      (field) => !String(payload[field] || "").trim()
    );
    if (missing.length > 0) {
      await popup.alert({
        icon: "warning",
        message: "Preencha todos os campos obrigatórios.",
      });
      return;
    }

    if (!payload.diasSemana.length) {
      await popup.alert({
        icon: "warning",
        message: "Selecione ao menos um dia da semana.",
      });
      return;
    }

    const cpfError = validateCpf(payload.cpf);
    if (cpfError) {
      await popup.alert({ icon: "warning", message: cpfError });
      return;
    }

    onProximo(payload);
  };

  return (
    <>
      <StatusBar>
        <SpecialtyName>{especialidade}</SpecialtyName>
        <StatusBlock>
          <img src={concedidoPqn} alt="Concedido" />
          <span>
            Vaga <em>concedida</em> pelo NEPS
          </span>
        </StatusBlock>
      </StatusBar>

      <FormWrapper>
        <FormHeader>Cadastre o aluno</FormHeader>

        <FieldGroup>
          <FormLabel htmlFor="nome">Nome do aluno*</FormLabel>
          <FormInput id="nome" value={form.nome} onChange={handleChange("nome")} />
        </FieldGroup>

        <TwoColumnRow>
          <FieldGroup>
            <FormLabel htmlFor="cpf">CPF do aluno*</FormLabel>
            <FormInput id="cpf" value={form.cpf} onChange={handleChange("cpf")} />
          </FieldGroup>
          <FieldGroup>
            <FormLabel htmlFor="dataNascimento">Data de Nascimento*</FormLabel>
            <FormInput
              id="dataNascimento"
              type="date"
              value={form.dataNascimento}
              onChange={handleChange("dataNascimento")}
            />
          </FieldGroup>
        </TwoColumnRow>

        <TwoColumnRow>
          <FieldGroup>
            <FormLabel htmlFor="numeroMatricula">Número de matrícula*</FormLabel>
            <FormInput
              id="numeroMatricula"
              value={form.numeroMatricula}
              onChange={handleChange("numeroMatricula")}
            />
          </FieldGroup>
          <FieldGroup>
            <FormLabel htmlFor="localRealizacao">Local de realização do estágio*</FormLabel>
            <ReadOnlyInput id="localRealizacao" value={localRealizacao} readOnly />
          </FieldGroup>
        </TwoColumnRow>

        <TwoColumnRow>
          <FieldGroup>
            <FormLabel htmlFor="inicioEstagio">Início do estágio*</FormLabel>
            <ReadOnlyInput id="inicioEstagio" value={inicioEstagio} readOnly />
          </FieldGroup>
          <FieldGroup>
            <FormLabel htmlFor="terminoEstagio">Término do estágio*</FormLabel>
            <ReadOnlyInput id="terminoEstagio" value={terminoEstagio} readOnly />
          </FieldGroup>
        </TwoColumnRow>

        <DaysWrapper>
          <FormLabel as="span">Dias da semana*</FormLabel>
          <DaysList>
            {DIAS_SEMANA.map((dia) => (
              <DayLabel key={dia}>
                <input
                  type="checkbox"
                  checked={form.diasSemana.includes(dia)}
                  onChange={() => toggleDia(dia)}
                />
                {dia}
              </DayLabel>
            ))}
          </DaysList>
        </DaysWrapper>

        <TwoColumnRow>
          <FieldGroup>
            <FormLabel>Horário de realização do estágio*</FormLabel>
            <HorarioRow>
              <FormInput
                id="horarioInicio"
                type="time"
                value={form.horarioInicio}
                onChange={handleChange("horarioInicio")}
              />
              <HorarioSeparator>—</HorarioSeparator>
              <FormInput
                id="horarioFim"
                type="time"
                value={form.horarioFim}
                onChange={handleChange("horarioFim")}
              />
            </HorarioRow>
            <FieldHint>Ex.: 09:00 — 12:00</FieldHint>
          </FieldGroup>
          <FieldGroup>
            <FormLabel htmlFor="cargaHorariaTotal">Carga horária total do estágio*</FormLabel>
            <ReadOnlyInput
              id="cargaHorariaTotal"
              value={cargaHorariaTotal !== "" ? `${cargaHorariaTotal}h` : ""}
              readOnly
            />
          </FieldGroup>
        </TwoColumnRow>

        <FormFooter>
          <VoltarButton type="button" onClick={onVoltar}>
            Voltar
          </VoltarButton>
          <ProximoButton type="button" onClick={handleSubmit}>
            Próximo ›
          </ProximoButton>
        </FormFooter>
      </FormWrapper>
    </>
  );
};

export default CadastrarAlunoForm;
